/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useDomainContext } from "@hooks/useDomainContext";
import { getAccessToken, getVisitId } from "@lib/shared/cookies";
import Script from "next/script";
import { ReactElement, useEffect, useState } from "react";

declare global {
    interface Window {
        inspector?: any;
        __inspectorInterval?: number;
        getAccessToken?: () => string | null;
        getVisitId?: () => string | null;
    }
}

const InspectorScript = ({
    stopInterval,
}: {
    stopInterval?: boolean;
}): ReactElement => {
    const { domain } = useDomainContext();

    const [inspectorReady, setInspectorReady] = useState(false);

    /* ===============================
       Stop interval when instructed
       =============================== */
    useEffect(() => {
        if (!stopInterval) return;

        const timeout = setTimeout(() => {
            if (window.__inspectorInterval) {
                clearInterval(window.__inspectorInterval);
                window.__inspectorInterval = undefined;
            }
        }, 10_000);

        return () => clearTimeout(timeout);
    }, [stopInterval]);

    /* ===============================
       Expose access helpers to window
       =============================== */
    useEffect(() => {
        window.getAccessToken = getAccessToken;
        window.getVisitId = getVisitId;
    }, []);

    /* ===============================
       Wait for inspector to load
       =============================== */
    useEffect(() => {
        let cancelled = false;

        const checkInspector = () => {
            if (cancelled) return;

            if (window.inspector) {
                setInspectorReady(true);
            } else {
                requestAnimationFrame(checkInspector);
            }
        };

        checkInspector();

        return () => {
            cancelled = true;
        };
    }, []);

    /* ===============================
       Cleanup on unmount
       =============================== */
    useEffect(() => {
        return () => {
            if (window.__inspectorInterval) {
                clearInterval(window.__inspectorInterval);
                window.__inspectorInterval = undefined;
            }
        };
    }, []);

    return (
        <>
            {/* Load inspector library */}
            <Script
                strategy="afterInteractive"
                src="https://cdn.trafficbox.com/assets/inspector.min.js"
            />

            {inspectorReady && (
                <Script
                    id="inspector-init"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
(function () {
    let events = [];
    const INTERVAL_TIME = 10000;
    const MAX_PAYLOAD_SIZE = 500000; // 500KB

    inspector.record({
        emit(event) {
            events.push(event);
        },
    });

    function sendBatch(batchEvents, batchNumber, totalBatches) {
        const token = window.getAccessToken?.();
        const visitId = window.getVisitId?.();

        if (!token || !visitId) {
            console.warn('[Inspector] Missing token or visitId, skipping batch', batchNumber);
            events.unshift(...batchEvents);
            return Promise.resolve();
        }

        return fetch("/api/inspector", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token,
            },
            body: JSON.stringify({
                events: batchEvents,
                visitorId: visitId,
                domain: "${domain.name}",
            }),
        }).catch(err => {
            console.error('[Inspector] Batch failed', batchNumber, err);
            events.unshift(...batchEvents); // re-queue
        });
    }

    function save() {
        if (!events.length) return;

        const token = window.getAccessToken?.();
        const visitId = window.getVisitId?.();

        if (!token || !visitId) {
            console.warn('[Inspector] Token or visitId missing, retrying later');
            return;
        }

        try {
            const payload = {
                events,
                visitorId: visitId,
                domain: "${domain.name}",
            };

            const body = JSON.stringify(payload);

            if (body.length > MAX_PAYLOAD_SIZE) {
                const batchesCount = Math.ceil(body.length / MAX_PAYLOAD_SIZE);
                const eventsPerBatch = Math.ceil(events.length / batchesCount);
                const batches = [];

                for (let i = 0; i < events.length; i += eventsPerBatch) {
                    batches.push(events.slice(i, i + eventsPerBatch));
                }

                batches.forEach((batch, i) =>
                    sendBatch(batch, i + 1, batches.length)
                );
            } else {
                fetch("/api/inspector", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + token,
                    },
                    body,
                }).catch(err => {
                    console.error('[Inspector] Save failed, retrying next tick', err);
                });
            }

            events = [];
        } catch (err) {
            console.error('[Inspector] Save error, retrying later', err);
        }
    }

    window.__inspectorInterval = setInterval(save, INTERVAL_TIME);
})();
                        `,
                    }}
                />
            )}
        </>
    );
};

export default InspectorScript;
